import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Avatar, Alert, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Divider, InputAdornment, ToggleButtonGroup, ToggleButton,
  Tabs, Tab,
} from '@mui/material';
import {
  Add, Edit, Delete, Search, PersonAdd, CheckCircle,
  Close, PhoneInTalk, AccessTime, TrendingUp,
  ViewColumn as KanbanIcon, TableChart as TableIcon,
  FileDownload, Block, Facebook,
} from '@mui/icons-material';
import { leadsAPI, usersAPI } from '../services/api';
import { useAppData } from '../context/AppDataContext';
import { maskPhone } from '../utils/masking';
import PaginationBar from '../components/PaginationBar';
import PageHeader from '../components/PageHeader';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';


const STATUS_CFG = {
  new:        { label: 'New',        color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  contacted:  { label: 'Contacted',  color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' },
  interested: { label: 'Interested', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  trial:      { label: 'Trial',      color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  converted:  { label: 'Converted',  color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  lost:       { label: 'Lost',       color: 'text.secondary', bg: '#F9FAFB', border: '#E5E7EB' },
};

const SOURCES = ['walk_in','referral','instagram','facebook','google','website','phone','other'];
const STATUSES = ['new','contacted','interested','trial','converted','lost'];
const KANBAN_COLS = ['new','contacted','interested','trial','converted','lost'];

function StatusChip({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.new;
  return (
    <Chip label={c.label} size="small"
      sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 11 }} />
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const emptyForm = {
  name: '', phone: '', email: '', source: 'walk_in',
  status: 'new', interest: '', notes: '', nextFollowUpDate: '',
};

export default function Leads() {
  const { gyms } = useAppData();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isSuperadmin = currentUser?.role === 'superadmin';

  const [leads, setLeads]     = useState([]);
  const [allLeads, setAllLeads] = useState([]); // For kanban (no pagination)
  const [stats, setStats]     = useState({});
  const [loading, setLoading] = useState(true);
  const [alert, setAlert]     = useState(null);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [followUpToday, setFollowUpToday] = useState(false);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [total, setTotal]     = useState(0);
  const [viewMode, setViewMode] = useState('kanban'); // 'table' | 'kanban'
  // Dialogs

  const [formOpen, setFormOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);

  const [detailOpen, setDetailOpen]   = useState(false);
  const [detailLead, setDetailLead]   = useState(null);
  const [fuNote, setFuNote]           = useState('');
  const [fuDate, setFuDate]           = useState('');
  const [fuSaving, setFuSaving]       = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  // Convert dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [convertMode, setConvertMode] = useState('create'); // 'create' | 'link'
  const [convertMembership, setConvertMembership] = useState('');
  const [convertPassword, setConvertPassword] = useState('Welcome@123');
  const [convertUserId, setConvertUserId] = useState('');
  const [convertUserSearch, setConvertUserSearch] = useState('');
  const [convertUsers, setConvertUsers] = useState([]);
  const [convertSaving, setConvertSaving] = useState(false);
  const [modalAlert, setModalAlert] = useState(null);

  // Lost dialog
  const [lostOpen, setLostOpen] = useState(false);
  const [lostLead, setLostLead] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [lostSaving, setLostSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, search: search || undefined, status: statusFilter || undefined,
        source: sourceFilter || undefined, followUpToday: followUpToday ? 'true' : undefined };
      const [listRes, statsRes] = await Promise.all([
        leadsAPI.getAll(params),
        leadsAPI.getStats(),
      ]);
      setLeads(listRes.data.data || []);
      setTotal(listRes.data.total || 0);
      setPages(listRes.data.pages || 1);
      setStats(statsRes.data.data || {});

      // Load all leads for kanban view (without pagination)
      if (viewMode === 'kanban') {
        const allRes = await leadsAPI.getAll({ limit: 500, search: search || undefined,
          source: sourceFilter || undefined });
        setAllLeads(allRes.data.data || []);
      }
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to load leads' });
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sourceFilter, followUpToday, viewMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, sourceFilter, followUpToday]);

  const openAdd  = () => { setModalAlert(null); setEditLead(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (lead) => {
    setModalAlert(null);
    setEditLead(lead);
    setForm({
      name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      source: lead.source || 'walk_in', status: lead.status || 'new',
      interest: lead.interest || '', notes: lead.notes || '',
      nextFollowUpDate: lead.nextFollowUpDate ? lead.nextFollowUpDate.slice(0,10) : '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    setModalAlert(null);
    if (!form.name || !form.phone) { setModalAlert({ type: 'error', msg: 'Name and phone are required' }); return; }
    setSaving(true);
    try {
      if (editLead) {
        await leadsAPI.update(editLead._id, form);
      } else {
        await leadsAPI.create(form);
      }
      setAlert({ type: 'success', msg: `Lead ${editLead ? 'updated' : 'created'} successfully` });
      setModalAlert(null);
      setFormOpen(false);
      load();
    } catch (e) {
      setModalAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to save lead' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await leadsAPI.delete(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      setAlert({ type: 'success', msg: 'Lead deleted' });
      load();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to delete lead' });
    }
  };

  const openDetail = async (lead) => {
    try {
      const res = await leadsAPI.getOne(lead._id);
      setDetailLead(res.data.data);
      setFuNote(''); setFuDate('');
      setDetailOpen(true);
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to load lead detail' });
    }
  };

  const submitFollowUp = async () => {
    if (!fuNote.trim()) return;
    setFuSaving(true);
    try {
      await leadsAPI.addFollowUp(detailLead._id, { note: fuNote, nextFollowUp: fuDate || undefined });
      const res = await leadsAPI.getOne(detailLead._id);
      setDetailLead(res.data.data);
      setFuNote(''); setFuDate('');
      load();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to add follow-up' });
    } finally {
      setFuSaving(false);
    }
  };

  // ── Convert dialog ─────────────────────────────────────────────
  const openConvert = (lead) => {
    setConvertLead(lead);
    setConvertMode('create');
    setConvertMembership('');
    setConvertPassword('Welcome@123');
    setConvertUserId('');
    setConvertUserSearch('');
    setConvertUsers([]);
    setConvertOpen(true);
  };

  const searchUsers = async (q) => {
    setConvertUserSearch(q);
    if (q.length < 2) { setConvertUsers([]); return; }
    try {
      const res = await usersAPI.getAll({ search: q, limit: 10 });
      setConvertUsers(res.data.data || []);
    } catch { /* ignore */ }
  };

  const handleConvert = async () => {
    setConvertSaving(true);
    try {
      const body = {
        membershipType: convertMembership || undefined,
      };
      if (convertMode === 'link' && convertUserId) {
        body.userId = convertUserId;
      } else {
        body.password = convertPassword;
      }
      const res = await leadsAPI.convert(convertLead._id, body);
      const userName = res.data.convertedUser?.name || convertLead.name;
      setAlert({ type: 'success', msg: `🎉 "${userName}" converted to member successfully!` });
      setConvertOpen(false);
      load();
    } catch (e) {
      setAlert({ type: 'error', msg: e.response?.data?.message || 'Failed to convert lead' });
    } finally {
      setConvertSaving(false);
    }
  };

  // ── Mark Lost ──────────────────────────────────────────────────
  const openLost = (lead) => {
    setLostLead(lead);
    setLostReason('');
    setLostOpen(true);
  };

  const handleMarkLost = async () => {
    setLostSaving(true);
    try {
      await leadsAPI.markLost(lostLead._id, lostReason);
      setAlert({ type: 'success', msg: `Lead "${lostLead.name}" marked as lost` });
      setLostOpen(false);
      load();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to mark lead as lost' });
    } finally {
      setLostSaving(false);
    }
  };

  // ── CSV Export ─────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const res = await leadsAPI.export({ status: statusFilter || undefined, source: sourceFilter || undefined });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to export leads' });
    }
  };

  // ── Kanban status change ───────────────────────────────────────
  const handleKanbanStatusChange = async (lead, newStatus) => {
    if (newStatus === 'converted') {
      openConvert(lead);
      return;
    }
    if (newStatus === 'lost') {
      openLost(lead);
      return;
    }
    try {
      await leadsAPI.update(lead._id, { status: newStatus });
      load();
    } catch (e) {
      setAlert({ type: 'error', msg: 'Failed to update status' });
    }
  };

  const statCards = [
    { label: 'New',       value: stats.new        || 0, color: '#1D4ED8' },
    { label: 'Contacted', value: stats.contacted   || 0, color: '#0891B2' },
    { label: 'Interested',value: stats.interested  || 0, color: '#D97706' },
    { label: 'Trial',     value: stats.trial       || 0, color: '#7C3AED' },
    { label: 'Converted', value: stats.converted   || 0, color: '#16A34A' },
    { label: 'Follow Today', value: stats.followUpToday || 0, color: '#DC2626' },
  ];

  // ── Kanban View Component ─────────────────────────────────────
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const lead = allLeads.find(l => l._id === draggableId);
    const newStatus = destination.droppableId;

    if (newStatus !== 'converted' && newStatus !== 'lost') {
      setAllLeads(prev => prev.map(l => l._id === draggableId ? { ...l, status: newStatus } : l));
    }
    handleKanbanStatusChange(lead, newStatus);
  };

  const KanbanView = () => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, minHeight: 400 }}>
        {KANBAN_COLS.map(status => {
          const cfg = STATUS_CFG[status];
          const colLeads = allLeads.filter(l => l.status === status);
          return (
            <Droppable droppableId={status} key={status}>
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ minWidth: 260, maxWidth: 280, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  {/* Column header */}
                  <Paper elevation={0} sx={{
                    p: 1.5, mb: 1.5, borderRadius: 2, border: '1px solid',
                    borderColor: 'divider', bgcolor: cfg.bg, flexShrink: 0
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: cfg.color }}>
                        {cfg.label}
                      </Typography>
                      <Box sx={{
                        px: 1, py: 0.25, borderRadius: 1.5,
                        bgcolor: cfg.color + '18', minWidth: 24, textAlign: 'center',
                      }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: cfg.color }}>
                          {colLeads.length}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Cards */}
                  <Stack spacing={1.25} sx={{ flexGrow: 1, minHeight: 100 }}>
                    {colLeads.length === 0 && (
                      <Paper elevation={0} sx={{
                        p: 3, borderRadius: 2, border: '1px dashed',
                        borderColor: 'divider', textAlign: 'center',
                      }}>
                        <Typography variant="caption" color="text.disabled">
                          No leads
                        </Typography>
                      </Paper>
                    )}
                    {colLeads.map((lead, index) => (
                      <Draggable key={lead._id} draggableId={lead._id} index={index}>
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            elevation={snapshot.isDragging ? 4 : 0}
                            sx={{
                              p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                              bgcolor: 'background.paper',
                              cursor: 'pointer', transition: 'all 0.15s',
                              opacity: snapshot.isDragging ? 0.9 : 1,
                              '&:hover': { borderColor: cfg.color + '60', boxShadow: `0 4px 12px ${cfg.color}15`, transform: 'translateY(-1px)' },
                            }} onClick={() => openDetail(lead)}>
                            <Stack direction="row" spacing={1.25} alignItems="center" mb={1}>
                              <Avatar sx={{ width: 30, height: 30, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'text.primary' }}>
                                {lead.name?.charAt(0)?.toUpperCase()}
                              </Avatar>
                              <Box sx={{ overflow: 'hidden' }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{lead.name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{maskPhone(lead.phone)}</Typography>
                              </Box>
                            </Stack>
                            {lead.interest && (
                              <Chip label={lead.interest} size="small" variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20, mb: 1 }} />
                            )}
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                {lead.source?.replace('_', ' ')}
                              </Typography>
                              {lead.nextFollowUpDate && (
                                <>
                                  <Typography variant="caption" color="text.disabled">·</Typography>
                                  <AccessTime sx={{ fontSize: 11, color: new Date(lead.nextFollowUpDate) < new Date() ? '#DC2626' : '#6B7280' }} />
                                  <Typography variant="caption" color={new Date(lead.nextFollowUpDate) < new Date() ? 'error' : 'text.secondary'}>
                                    {fmtDate(lead.nextFollowUpDate)}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                            {/* Status move buttons removed in favor of DND */}
                          </Paper>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                </Box>
              )}
            </Droppable>
          );
        })}
      </Box>
    </DragDropContext>
  );

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="Lead Management"
        subtitle="Track, convert and acquire leads via Meta Ads (Facebook & Instagram)"
        gradient="linear-gradient(90deg, #1D4ED8, #60A5FA)"
        actions={<>
          <ToggleButtonGroup size="small" value={viewMode} exclusive
            onChange={(_, v) => v && setViewMode(v)} sx={{ height: 32 }}>
            <ToggleButton value="table"><Tooltip title="Table view"><TableIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
            <ToggleButton value="kanban"><Tooltip title="Pipeline view"><KanbanIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" size="small" startIcon={<FileDownload />} onClick={handleExport}>Export CSV</Button>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openAdd}>Add Lead</Button>
        </>}
      />


      {/* Stat cards */}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3,1fr)', sm: 'repeat(6,1fr)' }, gap: 1.5, mb: 3 }}>
            {statCards.map(s => (
              <Paper key={s.label} elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2,
                cursor: s.label === 'Follow Today' ? 'pointer' : 'default',
                bgcolor: s.label === 'Follow Today' && followUpToday ? '#FEF2F2' : 'white' }}
                onClick={() => s.label === 'Follow Today' && setFollowUpToday(v => !v)}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>{s.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2.5}>
            <TextField placeholder="Search name, phone, email…" value={search}
              onChange={e => setSearch(e.target.value)} size="small" sx={{ flexGrow: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }} />
            {viewMode === 'table' && (
              <TextField
              select
              size="small"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => selected === '' ? 'All Statuses' : STATUS_CFG[selected]?.label || selected,
              }}
            >
                <MenuItem value="">All Statuses</MenuItem>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{STATUS_CFG[s].label}</MenuItem>)}
              </TextField>
            )}
            <TextField
              select
              size="small"
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              sx={{ minWidth: 140 }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => selected === '' ? 'All Sources' : selected.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
              }}
            >
              <MenuItem value="">All Sources</MenuItem>
              {SOURCES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
            </TextField>
          </Stack>

          {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}

      {/* View switcher */}
          {viewMode === 'kanban' ? <KanbanView /> : (
        <>
          {/* Table */}
          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lead</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Interest</TableCell>
                  <TableCell>Next Follow-up</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Stack direction="row" spacing={1.5} alignItems="center">
                      <Skeleton variant="circular" width={34} height={34} />
                      <Box><Skeleton width={110} height={14} sx={{ mb: 0.5 }} /><Skeleton width={90} height={12} /></Box>
                    </Stack></TableCell>
                    {[...Array(5)].map((__, j) => <TableCell key={j}><Skeleton width={80} /></TableCell>)}
                    <TableCell align="right"><Skeleton width={70} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                )) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 10 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                          <PersonAdd sx={{ fontSize: 36, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>No leads found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Start capturing leads to fill your pipeline</Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add First Lead</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : leads.map(lead => (
                  <TableRow key={lead._id} sx={{ cursor: 'pointer' }} onClick={() => openDetail(lead)}>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700, bgcolor: 'text.primary' }}>
                          {lead.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{lead.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{maskPhone(lead.phone)}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }} color="text.secondary">
                        {lead.source?.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell><StatusChip status={lead.status} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                        {lead.interest || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lead.nextFollowUpDate ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTime sx={{ fontSize: 13, color: new Date(lead.nextFollowUpDate) < new Date() ? '#DC2626' : '#6B7280' }} />
                          <Typography variant="caption" color={new Date(lead.nextFollowUpDate) < new Date() ? 'error' : 'text.secondary'}>
                            {fmtDate(lead.nextFollowUpDate)}
                          </Typography>
                        </Stack>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{fmtDate(lead.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        {lead.status !== 'converted' && lead.status !== 'lost' && (
                          <>
                            <Tooltip title="Convert to member">
                              <IconButton size="small" onClick={() => openConvert(lead)}
                                sx={{ color: 'text.secondary', '&:hover': { color: '#16A34A', bgcolor: '#F0FDF4' } }}>
                                <CheckCircle sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as lost">
                              <IconButton size="small" onClick={() => openLost(lead)}
                                sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                                <Block sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(lead)}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}>
                            <Edit sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: lead._id })}
                            sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                            <Delete sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}

      {/* ── Dialogs ───────────────────────────────────────────── */}


      {/* Add/Edit Lead Dialog */}
      <Dialog open={formOpen} onClose={() => { setFormOpen(false); setModalAlert(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editLead ? 'Edit Lead' : 'Add Lead'}</Typography>
            <IconButton size="small" onClick={() => { setFormOpen(false); setModalAlert(null); }}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {modalAlert && (
              <Alert severity={modalAlert.type} onClose={() => setModalAlert(null)} sx={{ borderRadius: 2 }}>
                {modalAlert.msg}
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                Contact Info
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone *" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </Grid>
              </Grid>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                Lead Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Source" value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                    {SOURCES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_',' ')}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {STATUSES.map(s => <MenuItem key={s} value={s}>{STATUS_CFG[s].label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Interested in" placeholder="e.g. Weight Loss, Yoga" value={form.interest}
                    onChange={e => setForm(f => ({ ...f, interest: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Next Follow-up Date" type="date" value={form.nextFollowUpDate}
                    onChange={e => setForm(f => ({ ...f, nextFollowUpDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Notes" multiline rows={2} value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFormOpen(false); setModalAlert(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editLead ? 'Update Lead' : 'Create Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail / Follow-up Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'text.primary', fontSize: '0.875rem', fontWeight: 700 }}>
                {detailLead?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6">{detailLead?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{maskPhone(detailLead?.phone)}</Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setDetailOpen(false)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLead && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <StatusChip status={detailLead.status} />
                <Chip label={detailLead.source?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize' }} />
                {detailLead.interest && <Chip label={detailLead.interest} size="small" variant="outlined" />}
              </Stack>
              {detailLead.notes && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Notes</Typography>
                  <Typography variant="body2">{detailLead.notes}</Typography>
                </Paper>
              )}
              {detailLead.lostReason && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, borderColor: '#FECACA', bgcolor: '#FEF2F2' }}>
                  <Typography variant="caption" color="error" fontWeight={600}>Lost Reason</Typography>
                  <Typography variant="body2">{detailLead.lostReason}</Typography>
                </Paper>
              )}
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>Follow-up History</Typography>
              {detailLead.followUps?.length === 0 && (
                <Typography variant="body2" color="text.secondary">No follow-ups yet.</Typography>
              )}
              {[...(detailLead.followUps || [])].reverse().map(fu => (
                <Paper key={fu._id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">{fmtDate(fu.doneAt)}</Typography>
                    {fu.nextFollowUp && (
                      <Chip label={`Next: ${fmtDate(fu.nextFollowUp)}`} size="small"
                        sx={{ bgcolor: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A', fontSize: 10 }} />
                    )}
                  </Stack>
                  <Typography variant="body2">{fu.note}</Typography>
                </Paper>
              ))}
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>Add Follow-up</Typography>
              <TextField fullWidth multiline rows={2} label="Note *" value={fuNote}
                onChange={e => setFuNote(e.target.value)} placeholder="What happened in this interaction?" />
              <TextField fullWidth label="Next Follow-up Date" type="date" value={fuDate}
                onChange={e => setFuDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          <Button variant="contained" onClick={submitFollowUp} disabled={fuSaving || !fuNote.trim()}>
            {fuSaving ? 'Saving…' : 'Add Follow-up'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Convert Lead Dialog ──────────────────────────────────── */}
      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUp sx={{ color: '#16A34A' }} />
              </Box>
              <Box>
                <Typography variant="h6">Convert to Member</Typography>
                <Typography variant="caption" color="text.secondary">
                  {convertLead?.name} — {maskPhone(convertLead?.phone)}
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small" onClick={() => setConvertOpen(false)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} mt={0.5}>
            {/* Mode toggle */}
            <ToggleButtonGroup size="small" value={convertMode} exclusive
              onChange={(_, v) => v && setConvertMode(v)} fullWidth
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' } }}>
              <ToggleButton value="create">Create new member</ToggleButton>
              <ToggleButton value="link">Link to existing user</ToggleButton>
            </ToggleButtonGroup>

            {convertMode === 'create' ? (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                  New Member Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                    A new user account will be created with name "<strong>{convertLead?.name}</strong>" and email "<strong>{convertLead?.email || `${maskPhone(convertLead?.phone)}@lead.fitistic.app`}</strong>"
                  </Alert>
                  <TextField fullWidth label="Initial Password" value={convertPassword}
                    onChange={e => setConvertPassword(e.target.value)}
                    helperText="The member can change this after first login" />
                  <TextField select fullWidth label="Membership Type" value={convertMembership}
                    onChange={e => setConvertMembership(e.target.value)}>
                    <MenuItem value="">— Select (optional) —</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </TextField>
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                  Link to Existing User
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <TextField fullWidth placeholder="Search by name or email…" value={convertUserSearch}
                    onChange={e => searchUsers(e.target.value)} size="small"
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
                  {convertUsers.length > 0 && (
                    <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', borderRadius: 1.5 }}>
                      {convertUsers.map(u => (
                        <Box key={u._id} sx={{
                          p: 1.25, cursor: 'pointer', borderBottom: '1px solid',
                          borderColor: 'divider', transition: 'all 0.1s',
                          bgcolor: convertUserId === u._id ? '#EFF6FF' : 'transparent',
                          '&:hover': { bgcolor: 'background.paper' },
                          '&:last-child': { borderBottom: 0 },
                        }} onClick={() => setConvertUserId(u._id)}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'text.primary' }}>
                              {u.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                            </Box>
                            {convertUserId === u._id && (
                              <CheckCircle sx={{ fontSize: 16, color: '#16A34A', ml: 'auto' }} />
                            )}
                          </Stack>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  <TextField select fullWidth label="Membership Type" value={convertMembership}
                    onChange={e => setConvertMembership(e.target.value)}>
                    <MenuItem value="">— Select (optional) —</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </TextField>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConvert} disabled={convertSaving || (convertMode === 'link' && !convertUserId)}>
            {convertSaving ? 'Converting…' : 'Convert Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Mark Lost Dialog ─────────────────────────────────────── */}
      <Dialog open={lostOpen} onClose={() => setLostOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Block sx={{ color: '#DC2626', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1rem' }}>Mark Lead as Lost</Typography>
              <Typography variant="caption" color="text.secondary">{lostLead?.name}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Why was this lead lost? This helps improve your sales process.
            </Typography>
            <TextField select fullWidth label="Reason" value={lostReason}
              onChange={e => setLostReason(e.target.value)}>
              <MenuItem value="">— Select a reason —</MenuItem>
              <MenuItem value="Not interested">Not interested</MenuItem>
              <MenuItem value="Too expensive">Too expensive</MenuItem>
              <MenuItem value="Joined competitor">Joined competitor</MenuItem>
              <MenuItem value="Location too far">Location too far</MenuItem>
              <MenuItem value="No response">No response / Unreachable</MenuItem>
              <MenuItem value="Timing not right">Timing not right</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
            {lostReason === 'Other' && (
              <TextField fullWidth label="Custom reason" multiline rows={2}
                onChange={e => setLostReason(e.target.value)} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleMarkLost}
            disabled={lostSaving || !lostReason}>
            {lostSaving ? 'Saving…' : 'Mark as Lost'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Lead?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">This will permanently delete the lead and all follow-up history.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
